import React from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useModal } from "../context/ModalContext";
import useScrollAnimation from "../hooks/useScrollAnimation";
import { DottedSurface } from "./ui/dotted-surface";

const consultingOutcomes = [
  "Prioritized AI opportunity map tied to business outcomes",
  "90-day execution roadmap with owners, milestones, and KPIs",
  "Reference architecture for models, data, tools, and integrations",
  "Governance framework for quality, risk, and compliance",
];

const consultingPhases = [
  {
    step: "01",
    title: "Business & Workflow Audit",
    detail:
      "We map processes, constraints, and data flow to identify where AI can create measurable lift first.",
  },
  {
    step: "02",
    title: "Use-Case Prioritization",
    detail:
      "Use cases are scored by impact, feasibility, risk, and time-to-value so teams focus on the right bets.",
  },
  {
    step: "03",
    title: "Execution Architecture",
    detail:
      "We define target architecture, integration patterns, guardrails, and delivery milestones for production readiness.",
  },
  {
    step: "04",
    title: "Rollout & Adoption Plan",
    detail:
      "You get a practical runbook for pilot launch, scale-up, team enablement, and ongoing optimization.",
  },
];

const Consulting = () => {
  const { openDemoModal } = useModal();
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.12 });

  return (
    <section className="relative py-24 md:py-28 bg-[--bg-primary] min-h-[70vh]">
      <div className="absolute top-0 left-0 right-0 section-divider" />

      <div ref={sectionRef} className="max-w-[1500px] mx-auto px-4 sm:px-6">
        <div className="relative overflow-hidden soft-card rounded-3xl p-8 md:p-10 lg:p-14">
          <DottedSurface className="opacity-40 [mask-image:radial-gradient(circle_at_76%_24%,black_15%,transparent_75%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_20%,var(--accent-glow)_0%,transparent_46%)]" />

          <div className="relative z-10 max-w-4xl">
            <p
              className={`section-eyebrow mb-5 transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
            >
              Consulting Track
            </p>
            <h1
              className={`text-[clamp(2rem,4.2vw,4rem)] font-semibold text-[--text-primary] leading-[1.02] transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
              style={{ transitionDelay: "80ms" }}
            >
              Consulting Built for Execution
            </h1>
            <p
              className={`mt-6 text-lg text-[--text-secondary] max-w-3xl transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
              style={{ transitionDelay: "160ms" }}
            >
              We partner with leadership and delivery teams to turn AI ambition into shipped systems.
              You get clear priorities, ownership, and an execution plan built for your operating
              model.
            </p>

            <div
              className={`mt-9 flex flex-wrap gap-4 transition-all duration-700 ease-out ${
                isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
              }`}
              style={{ transitionDelay: "240ms" }}
            >
              <button
                onClick={openDemoModal}
                className="px-7 py-3.5 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-all flex items-center gap-2 btn-pattern"
              >
                Book a Consulting Session
                <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#consulting-engagement"
                className="px-7 py-3.5 text-[11px] font-medium text-[--text-primary] uppercase tracking-[0.18em] font-mono bg-transparent border border-[--border] rounded-full hover:border-[--border-hover] hover:bg-[--bg-secondary] transition-colors"
              >
                See Engagement Model
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-[1.1fr_0.9fr] gap-8 lg:gap-10 items-start">
          <div
            className={`soft-card rounded-2xl p-7 lg:p-8 transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "300ms" }}
          >
            <p className="section-eyebrow mb-5">What You Walk Away With</p>
            <div className="space-y-4">
              {consultingOutcomes.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 mt-0.5 text-[--accent] shrink-0" />
                  <p className="text-[--text-secondary] leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>

          <div
            className={`soft-card rounded-2xl p-7 lg:p-8 transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "380ms" }}
          >
            <p className="section-eyebrow mb-5">Expected Outcomes</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-3xl font-semibold text-[--accent] leading-none">2-4</p>
                <p className="mt-2 text-sm text-[--text-secondary] uppercase tracking-[0.1em]">
                  week assessment cycle
                </p>
              </div>
              <div>
                <p className="text-3xl font-semibold text-[--accent] leading-none">Top 3</p>
                <p className="mt-2 text-sm text-[--text-secondary] uppercase tracking-[0.1em]">
                  prioritized use cases
                </p>
              </div>
              <div>
                <p className="text-3xl font-semibold text-[--accent] leading-none">90-Day</p>
                <p className="mt-2 text-sm text-[--text-secondary] uppercase tracking-[0.1em]">
                  execution roadmap
                </p>
              </div>
              <div>
                <p className="text-3xl font-semibold text-[--accent] leading-none">0</p>
                <p className="mt-2 text-sm text-[--text-secondary] uppercase tracking-[0.1em]">
                  strategy shelfware
                </p>
              </div>
            </div>
          </div>
        </div>

        <div id="consulting-engagement" className="mt-14">
          <div
            className={`soft-card rounded-2xl p-7 lg:p-10 transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "460ms" }}
          >
            <p className="section-eyebrow mb-6">Engagement Model</p>
            <div className="grid md:grid-cols-2 gap-5">
              {consultingPhases.map((phase) => (
                <article
                  key={phase.step}
                  className="rounded-2xl border border-[--border] bg-[--bg-secondary]/45 p-5"
                >
                  <p className="text-[11px] font-mono font-semibold text-[--accent] uppercase tracking-[0.16em]">
                    Phase {phase.step}
                  </p>
                  <h2 className="mt-2 text-xl font-semibold text-[--text-primary]">{phase.title}</h2>
                  <p className="mt-3 text-[--text-secondary] leading-relaxed">{phase.detail}</p>
                </article>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-14">
          <div
            className={`relative overflow-hidden soft-card rounded-3xl p-8 md:p-10 lg:p-14 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8 transition-all duration-700 ease-out ${
              isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
            style={{ transitionDelay: "540ms" }}
          >
            <DottedSurface className="opacity-45 [mask-image:radial-gradient(circle_at_70%_45%,black_14%,transparent_76%)]" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_28%,var(--accent-glow)_0%,transparent_48%)]" />

            <div className="relative z-10 max-w-3xl">
              <p className="section-eyebrow mb-4">Final Step</p>
              <h2 className="text-[clamp(1.9rem,3.8vw,3.4rem)] font-semibold text-[--text-primary] leading-[1.04]">
                Start with a conversation.
                <br />
                <span className="accent-wash">Leave with a plan.</span>
              </h2>
              <p className="mt-5 text-[--text-secondary] text-lg leading-relaxed">
                Start with a focused consulting sprint. We align on priorities, define the build
                path, and move into execution with your team.
              </p>
            </div>

            <div className="relative z-10 flex flex-wrap gap-4">
              <button
                onClick={openDemoModal}
                className="px-7 py-3.5 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-all inline-flex items-center gap-2 btn-pattern"
              >
                Book a Free Audit
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Consulting;
